import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Plus,
  Loader2,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Pencil,
  FileText,
  BellRing,
  Search,
  TableProperties,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  PRINT_BOOK_STATUSES,
  PRINT_BOOK_STATUS_LABELS,
  printBookApi,
  type PrintBookListItem,
  type PrintBookMaterialType,
  type PrintBookScopeLevel,
} from '@/api/printBook'
import type { BookListRouteState } from './bookNavigation'
import {
  getPrintBookDeleteErrorMessage,
  PRINT_BOOK_DELETE_CONFIRMATION_NOTICE,
} from './printBookDeletion'
import { buildPrintBookSeries, type PrintBookSeries } from './printBookSeries'
import { ProgressAlertSettingsDialog } from './ProgressAlertSettingsDialog'

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: { label: PRINT_BOOK_STATUS_LABELS.draft, color: 'bg-slate-100 text-slate-600' },
  ready: { label: PRINT_BOOK_STATUS_LABELS.ready, color: 'bg-yellow-100 text-yellow-700' },
  completed: { label: PRINT_BOOK_STATUS_LABELS.completed, color: 'bg-green-100 text-green-700' },
}

const MATERIAL_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  CHECKUP_BOOK: { label: '체크업북', color: 'bg-sky-100 text-sky-700' },
  WORKBOOK: { label: '워크북', color: 'bg-emerald-100 text-emerald-700' },
}

const SERIES_STATUS_CONFIG = {
  completed: { label: '전체 입고', color: 'bg-green-100 text-green-700' },
  ready: { label: '일부 발주·입고', color: 'bg-yellow-100 text-yellow-700' },
  draft: { label: '전체 미발주', color: 'bg-slate-100 text-slate-600' },
}

function getScopeLabel(book: PrintBookListItem) {
  if (book.scopeLevel === 'GLOBAL') return '전체'
  if (book.scopeLevel === 'CURRICULUM') return `커리큘럼 #${book.scopeContentId}`
  if (book.scopeLevel === 'COURSE') return `코스 #${book.scopeContentId}`
  return '-'
}

function getBookSearchTarget(book: PrintBookListItem) {
  return [book.title, book.subtitle, book.bookLabel, book.volumeLabel]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

function getSeriesTitle(series: PrintBookSeries) {
  return series.head.bookLabel || series.head.title
}

function getSeriesScopeLabel(series: PrintBookSeries) {
  const first = getScopeLabel(series.head)
  return series.books.every(book => getScopeLabel(book) === first) ? first : '혼합'
}

function getSeriesTotalPageCount(series: PrintBookSeries) {
  const pageCounts = series.books.map(book => book.totalPageCount).filter((count): count is number => count != null)
  if (pageCounts.length === 0) return null
  return pageCounts.reduce((sum, count) => sum + count, 0)
}

function getSeriesStatus(series: PrintBookSeries) {
  if (series.books.every(book => book.status === 'completed')) return SERIES_STATUS_CONFIG.completed
  if (series.books.some(book => book.status === 'ready' || book.status === 'completed')) return SERIES_STATUS_CONFIG.ready
  return SERIES_STATUS_CONFIG.draft
}

export function BookListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const routeState = location.state as BookListRouteState | null
  const scrollRestoredRef = useRef(false)
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '')
  const [materialFilter, setMaterialFilter] = useState<PrintBookMaterialType | ''>(() => (searchParams.get('material') as PrintBookMaterialType | null) ?? '')
  const [scopeFilter, setScopeFilter] = useState<PrintBookScopeLevel | ''>(() => (searchParams.get('scope') as PrintBookScopeLevel | null) ?? '')
  const [noticeFilter, setNoticeFilter] = useState<'all' | 'enabled' | 'disabled'>(() => (searchParams.get('notice') as 'enabled' | 'disabled' | null) ?? 'all')
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '')
  const [progressAlertSettingsOpen, setProgressAlertSettingsOpen] = useState(false)
  const [sortKey, setSortKey] = useState<'createdDesc' | 'createdAsc' | 'bookLabel' | 'volumeLabel' | 'materialType'>(() => (
    searchParams.get('sort') as 'createdDesc' | 'createdAsc' | 'bookLabel' | 'volumeLabel' | 'materialType' | null
  ) ?? 'createdDesc')
  const [expandedSeriesIds, setExpandedSeriesIds] = useState<Set<number>>(() => new Set(
    (searchParams.get('expanded') ?? '')
      .split(',')
      .map(Number)
      .filter(Number.isInteger),
  ))

  const { data: books, isLoading, error } = useQuery({
    queryKey: ['print-books'],
    queryFn: printBookApi.getAll,
  })

  const deleteMutation = useMutation({
    mutationFn: (bookId: number) => printBookApi.delete(bookId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-books'] })
      toast.success('교재가 삭제되었습니다.')
    },
    onError: (deleteError) => {
      toast.error(getPrintBookDeleteErrorMessage(deleteError))
    },
  })

  useEffect(() => {
    if (isLoading || scrollRestoredRef.current || routeState?.restoreScrollY == null) return
    scrollRestoredRef.current = true
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.scrollTo({ top: routeState.restoreScrollY, behavior: 'auto' }))
    })
  }, [isLoading, routeState?.restoreScrollY])

  const sortedBooks = useMemo(() => {
    return [...(books ?? [])].sort((a, b) => {
      if (sortKey === 'createdAsc') return a.createdAt.localeCompare(b.createdAt)
      if (sortKey === 'bookLabel') return a.bookLabel.localeCompare(b.bookLabel)
      if (sortKey === 'volumeLabel') return (a.volumeLabel ?? '').localeCompare(b.volumeLabel ?? '')
      if (sortKey === 'materialType') return (a.materialType ?? '').localeCompare(b.materialType ?? '')
      return b.createdAt.localeCompare(a.createdAt)
    })
  }, [books, sortKey])

  const printBookSeries = useMemo(() => buildPrintBookSeries(sortedBooks), [sortedBooks])

  const filteredSeries = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    return printBookSeries.filter(series => (
      series.books.some(book => {
        const keywordMatches = keyword.length === 0 || getBookSearchTarget(book).includes(keyword)
        const materialMatches = !materialFilter || book.materialType === materialFilter
        const scopeMatches = !scopeFilter || book.scopeLevel === scopeFilter
        const noticeMatches = noticeFilter === 'all' || book.noticeEnabled === (noticeFilter === 'enabled')
        const statusMatches = !statusFilter || book.status === statusFilter
        return keywordMatches && materialMatches && scopeMatches && noticeMatches && statusMatches
      })
    ))
  }, [materialFilter, noticeFilter, printBookSeries, scopeFilter, search, statusFilter])

  const updateListParam = (key: string, value: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      if (value) next.set(key, value)
      else next.delete(key)
      return next
    }, { replace: true })
  }

  const openBook = (bookId: number) => {
    navigate(`/contents/books/${bookId}`, {
      state: {
        returnTo: `${location.pathname}${location.search}`,
        listScrollY: window.scrollY,
        activeBookId: bookId,
      } satisfies BookListRouteState,
    })
  }

  const toggleSeries = (seriesId: number) => {
    const next = new Set(expandedSeriesIds)
    if (next.has(seriesId)) next.delete(seriesId)
    else next.add(seriesId)
    setExpandedSeriesIds(next)
    updateListParam('expanded', [...next].join(','))
  }

  const handleDelete = (book: PrintBookListItem) => {
    const label = book.volumeLabel ? `${book.title} (${book.volumeLabel})` : book.title
    if (!window.confirm(`「${label}」 교재를 삭제하시겠습니까?\n\n${PRINT_BOOK_DELETE_CONFIRMATION_NOTICE}`)) {
      return
    }
    deleteMutation.mutate(book.id)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 text-red-700 bg-red-50 rounded-lg">
        <AlertCircle className="w-5 h-5" />
        <span>교재 목록을 불러오는데 실패했습니다.</span>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">교재 관리</h1>
            <p className="text-sm text-slate-500">커리큘럼 기반 워크북/체크업북을 관리합니다</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setProgressAlertSettingsOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 border border-slate-300 bg-white text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            <BellRing className="w-4 h-4" />
            진도 알림 설정
          </button>
          <button
            onClick={() => navigate('/contents/books/bulk-new')}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors"
          >
            <TableProperties className="w-4 h-4" />
            교재 일괄 생성
          </button>
          <button
            onClick={() => navigate('/contents/books/new', {
              state: {
                returnTo: `${location.pathname}${location.search}`,
                listScrollY: window.scrollY,
              } satisfies BookListRouteState,
            })}
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors"
          >
            <Plus className="w-4 h-4" />
            새 교재 만들기
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid grid-cols-1 lg:grid-cols-7 gap-3">
        <label className="lg:col-span-2 relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={event => { setSearch(event.target.value); updateListParam('q', event.target.value) }} placeholder="제목, 라벨, 권 검색" className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </label>
        <select value={materialFilter} onChange={event => { setMaterialFilter(event.target.value as PrintBookMaterialType | ''); updateListParam('material', event.target.value) }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
          <option value="">전체 유형</option>
          <option value="CHECKUP_BOOK">체크업북</option>
          <option value="WORKBOOK">워크북</option>
        </select>
        <select value={scopeFilter} onChange={event => { setScopeFilter(event.target.value as PrintBookScopeLevel | ''); updateListParam('scope', event.target.value) }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
          <option value="">전체 범위</option>
          <option value="GLOBAL">전체</option>
          <option value="CURRICULUM">커리큘럼</option>
          <option value="COURSE">코스</option>
        </select>
        <select value={noticeFilter} onChange={event => { setNoticeFilter(event.target.value as 'all' | 'enabled' | 'disabled'); updateListParam('notice', event.target.value === 'all' ? '' : event.target.value) }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
          <option value="all">알림 전체</option>
          <option value="enabled">알림 켬</option>
          <option value="disabled">알림 끔</option>
        </select>
        <select value={statusFilter} onChange={event => { setStatusFilter(event.target.value); updateListParam('status', event.target.value) }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
          <option value="">상태 전체</option>
          {PRINT_BOOK_STATUSES.map(status => (
            <option key={status} value={status}>{PRINT_BOOK_STATUS_LABELS[status]}</option>
          ))}
        </select>
        <select value={sortKey} onChange={event => { setSortKey(event.target.value as typeof sortKey); updateListParam('sort', event.target.value === 'createdDesc' ? '' : event.target.value) }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
          <option value="createdDesc">최신순</option>
          <option value="createdAsc">오래된순</option>
          <option value="bookLabel">라벨순</option>
          <option value="volumeLabel">권순</option>
          <option value="materialType">유형순</option>
        </select>
      </div>

      {/* Series List */}
      {books && books.length > 0 && filteredSeries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-slate-200">
          <FileText className="w-12 h-12 text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">조건에 맞는 교재군이 없습니다</p>
        </div>
      ) : books && books.length > 0 ? (
        <div className="space-y-3">
          {filteredSeries.map((series) => {
            const expanded = expandedSeriesIds.has(series.head.id)
            const materialCfg = series.head.materialType ? MATERIAL_TYPE_CONFIG[series.head.materialType] : null
            const seriesStatus = getSeriesStatus(series)
            const totalModules = series.books.reduce((sum, book) => sum + book.moduleCount, 0)
            const totalLessons = series.books.reduce((sum, book) => sum + book.lessonCount, 0)
            const totalPageCount = getSeriesTotalPageCount(series)
            const confirmedLessonCount = series.books.reduce((sum, book) => sum + book.confirmedLessonCount, 0)
            const totalLessonCount = series.books.reduce((sum, book) => sum + book.totalLessonCount, 0)
            const noticeEnabled = series.books.some(book => book.noticeEnabled)
            const hasExternalDesign = series.books.some(book => book.sourceType === 'EXTERNAL_DESIGN')
            const seriesBooksById = new Map(series.books.map(book => [book.id, book]))
            const containsLastOpenedBook = routeState?.activeBookId != null && series.books.some(book => book.id === routeState.activeBookId)

            return (
              <div key={series.head.id} className={`overflow-hidden rounded-xl border bg-white shadow-sm transition ${containsLastOpenedBook ? 'border-amber-400 ring-2 ring-amber-100' : 'border-slate-200'}`}>
                <div className="grid grid-cols-[36px_44px_minmax(0,1fr)_auto] items-center gap-2 px-3 py-3 xl:grid-cols-[44px_56px_minmax(220px,1fr)_112px_116px_96px_112px_96px] xl:gap-3 xl:px-4">
                  <button
                    type="button"
                    onClick={() => toggleSeries(series.head.id)}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                    aria-label={expanded ? '권 목록 접기' : '권 목록 펼치기'}
                  >
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  {series.head.coverImageUrl ? (
                    <img
                      src={series.head.coverImageUrl}
                      alt=""
                      className="h-16 w-11 rounded-md border border-slate-200 bg-white object-cover"
                    />
                  ) : (
                    <div className="flex h-16 w-11 items-center justify-center rounded-md border border-slate-200 bg-slate-50">
                      <BookOpen className="h-4 w-4 text-slate-300" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="mb-1 flex flex-wrap items-center gap-1.5">
                      <span className="truncate text-base font-semibold text-slate-900">{getSeriesTitle(series)}</span>
                      {materialCfg && (
                        <span className={`inline-block shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${materialCfg.color}`}>
                          {materialCfg.label}
                        </span>
                      )}
                      {hasExternalDesign && (
                        <span className="inline-block shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-medium text-violet-700">
                          외부 제작본
                        </span>
                      )}
                      {series.hasMissingLink && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700">
                          <AlertTriangle className="h-3 w-3" />
                          연결 누락
                        </span>
                      )}
                      {series.hasCycle && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-medium text-orange-700">
                          <AlertTriangle className="h-3 w-3" />
                          순환 연결
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      <span>대표 {series.head.volumeLabel || '1권'} · {series.head.title}</span>
                      <span>{getSeriesScopeLabel(series)}</span>
                      <span>{series.totalBookCount}권</span>
                      {series.continuesTo && (
                        <span>이어서 {series.continuesTo.bookLabel || series.continuesTo.title}</span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 xl:hidden">
                      <span>알림 {noticeEnabled ? '노출' : '꺼짐'}</span>
                      <span>{totalModules}모듈 · {totalLessons}레슨</span>
                      <span>{totalPageCount == null ? '-' : `${totalPageCount}p`}</span>
                      <span>컨펌 {confirmedLessonCount}/{totalLessonCount}</span>
                    </div>
                  </div>
                  <div className="hidden xl:block">
                    <div className="text-[11px] font-medium uppercase text-slate-400">알림</div>
                    <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${noticeEnabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {noticeEnabled ? '노출' : '꺼짐'}
                    </span>
                  </div>
                  <div className="hidden xl:block">
                    <div className="text-[11px] font-medium uppercase text-slate-400">구성</div>
                    <div className="mt-1 text-sm text-slate-700">{totalModules}모듈 · {totalLessons}레슨</div>
                  </div>
                  <div className="hidden xl:block">
                    <div className="text-[11px] font-medium uppercase text-slate-400">페이지</div>
                    <div className="mt-1 text-sm text-slate-700">{totalPageCount == null ? '-' : `${totalPageCount}p`}</div>
                  </div>
                  <div className="hidden xl:block">
                    <div className="text-[11px] font-medium uppercase text-slate-400">컨펌</div>
                    <div className="mt-1 text-sm text-slate-700">{confirmedLessonCount}/{totalLessonCount}</div>
                  </div>
                  <div className="flex items-center justify-end gap-1 xl:gap-2">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${seriesStatus.color}`}>
                      {seriesStatus.label}
                    </span>
                    <button
                      type="button"
                      onClick={() => openBook(series.head.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-amber-50 hover:text-amber-700"
                      title="대표 교재 편집"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-slate-200 bg-slate-50/70">
                    <div className="hidden grid-cols-[84px_minmax(220px,1fr)_150px_92px_92px_92px_92px] gap-3 border-b border-slate-200 px-4 py-2 text-xs font-semibold uppercase text-slate-400 xl:grid">
                      <div>권</div>
                      <div>교재명</div>
                      <div>다음권</div>
                      <div className="text-center">모듈</div>
                      <div className="text-center">페이지</div>
                      <div className="text-center">상태</div>
                      <div className="text-center">액션</div>
                    </div>
                    <div className="divide-y divide-slate-200">
                      {series.books.map((book, index) => {
                        const statusCfg = STATUS_CONFIG[book.status] || STATUS_CONFIG.draft
                        const allConfirmed = book.totalLessonCount > 0 && book.confirmedLessonCount === book.totalLessonCount
                        const nextBook = book.nextBookId == null ? null : seriesBooksById.get(book.nextBookId)
                        // 다른 계열로 이어지는 권은 "1권"만으로는 모호하므로 교재명을 그대로 보여준다.
                        const continuesToOtherSeries = series.continuesTo != null && book.nextBookId === series.continuesTo.id
                        const nextLabel = continuesToOtherSeries
                          ? series.continuesTo?.title
                          : nextBook ? nextBook.volumeLabel || nextBook.title : null

                        return (
                          <div key={book.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-sm xl:grid-cols-[84px_minmax(220px,1fr)_150px_92px_92px_92px_92px] xl:py-2.5">
                            <div className="hidden font-medium text-slate-700 xl:block">{book.volumeLabel || `${index + 1}권`}</div>
                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={() => openBook(book.id)}
                                className="block truncate text-left font-medium text-slate-800 hover:text-amber-700"
                              >
                                {book.title}
                              </button>
                              {book.subtitle && <div className="truncate text-xs text-slate-400">{book.subtitle}</div>}
                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 xl:hidden">
                                <span>{book.volumeLabel || `${index + 1}권`}</span>
                                <span>
                                  다음 {book.nextBookId == null
                                    ? '마지막'
                                    : nextLabel ?? `누락 #${book.nextBookId}`}
                                </span>
                                <span>{book.moduleCount}모듈</span>
                                <span>{book.totalPageCount ? `${book.totalPageCount}p` : '-'}</span>
                                <span className={`inline-flex items-center gap-1 ${allConfirmed ? 'text-green-600' : 'text-slate-400'}`}>
                                  {allConfirmed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                                  {statusCfg.label}
                                </span>
                              </div>
                            </div>
                            <div className="hidden text-xs text-slate-500 xl:block">
                              {book.nextBookId == null ? (
                                <span>마지막</span>
                              ) : nextLabel ? (
                                <span>{nextLabel}</span>
                              ) : (
                                <span className="font-medium text-red-600">누락 #{book.nextBookId}</span>
                              )}
                            </div>
                            <div className="hidden text-center text-slate-600 xl:block">{book.moduleCount}</div>
                            <div className="hidden text-center text-slate-600 xl:block">{book.totalPageCount ? `${book.totalPageCount}p` : '-'}</div>
                            <div className="hidden text-center xl:block">
                              <span className={`inline-flex items-center gap-1 text-xs ${allConfirmed ? 'text-green-600' : 'text-slate-400'}`}>
                                {allConfirmed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                                {statusCfg.label}
                              </span>
                            </div>
                            <div className="flex items-center justify-center gap-1">
                              <button
                                type="button"
                                onClick={() => openBook(book.id)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-white hover:text-amber-700"
                                title="편집"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(book)}
                                disabled={deleteMutation.isPending}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                                title="교재 삭제"
                                aria-label={`${book.title} 교재 삭제`}
                              >
                                {deleteMutation.isPending && deleteMutation.variables === book.id
                                  ? <Loader2 className="h-4 w-4 animate-spin" />
                                  : <Trash2 className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    {series.hasMissingLink && (
                      <div className="flex items-start gap-2 border-t border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>연결된 다음 권을 찾을 수 없습니다: {series.missingNextBookIds.map(id => `#${id}`).join(', ')}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-200">
          <FileText className="w-12 h-12 text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">아직 생성된 교재가 없습니다</p>
          <button
            onClick={() => navigate('/contents/books/new')}
            className="mt-4 flex items-center gap-2 px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            첫 번째 교재 만들기
          </button>
        </div>
      )}

      {progressAlertSettingsOpen ? (
        <ProgressAlertSettingsDialog onClose={() => setProgressAlertSettingsOpen(false)} />
      ) : null}
    </div>
  )
}
